import { t } from 'i18next';

import { MONTHLY_PERIOD_OPTION } from '../../constants/benefits';
import { DATE_DDMMYYYY_REGEX } from '../../constants/constants';
import { ISearchOptions } from '../../interfaces/benefits';
import type { DDMMYYYYValidation } from '../../types/types';
import { invertDateFormat } from '../utils';

export const getLastMonthOption = (): ISearchOptions => {
  const currentMonth = getLastMonths(1);
  return {
    dateRange: currentMonth[0],
    period: MONTHLY_PERIOD_OPTION
  };
};

export const getLastMonths = (monthsNumber = 12) => {
  const dates = [];
  let today = new Date();
  let monthCounter = 0;

  while (monthCounter <= monthsNumber) {
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = `${today.getDate()}`.padStart(2, '0');
    const formattedMonth = formatMonthNumber(currentMonth + 1);
    today.setMonth(currentMonth);
    today = new Date(today.getFullYear(), today.getMonth(), 0);
    if (currentDay === '01') {
      continue;
    }
    dates.push([`${currentYear}-${formattedMonth}-01`, `${currentYear}-${formattedMonth}-${currentDay}`]);
    monthCounter += 1;
  }
  return dates;
};

export const formatMonthNumber = (monthNumber: number) => {
  if (monthNumber < 10) {
    return `0${monthNumber}`;
  }
  return monthNumber;
};

export const getCurrentDate = () => {
  const today = new Date().toLocaleDateString('en-GB');
  return today;
};

export const getCurrentDateYYYYMMDD = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const formatToLocaleMonthDay = (date: string, locale = 'default') => {
  const splittedDate = date.split('-').map((datePart) => Number.parseInt(datePart, 10));
  const dateObject = new Date(splittedDate[0], splittedDate[1] - 1, splittedDate[2]);
  return dateObject.toLocaleString(locale, { month: 'short', day: 'numeric' });
};

export const invertDateStrFormat = (dateStr: string) => {
  if (!dateStr) return '';
  const delimiter = dateStr.includes('/') ? '/' : '-';
  const [year, month, day] = dateStr.split(delimiter);
  return `${day}${delimiter}${month}${delimiter}${year}`;
};

export const formatDateToDDMMYYYY = (dateString: string) => {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatDateMMDD = (dateString: string, useAbbreviation = true) => {
  const today = new Date();
  const inputDate = new Date(dateString + 'T00:00:00');

  const todayFormatted = formatDateToDDMMYYYY(today.toISOString().split('T')[0]);
  const inputDateFormatted = formatDateToDDMMYYYY(inputDate.toISOString().split('T')[0]);

  if (todayFormatted === inputDateFormatted) {
    return t('global.today.main');
  }

  const monthNameKey = `csp.months.${inputDate.toLocaleString('en-US', { month: 'long' }).toLowerCase()}`;
  let monthName = t(monthNameKey);

  if (useAbbreviation) {
    monthName = monthName.slice(0, 3).toLowerCase();
  }

  return `${inputDate.getDate()} ${monthName}`;
};

export const daysUntil = (date: string): number => {
  const today = new Date();
  const targetDate = new Date(date);
  const timeDifference = targetDate.getTime() - today.getTime();
  const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
  return daysDifference === 0 ? 0 : daysDifference;
};

export const formatDateYYYYMMDDTT = (date: Date, isEnd = false) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = isEnd ? '23:59:59' : '00:00:00';
  return `${year}-${month}-${day}T${time}`;
};

export const convertDDMMYYYYToISO = (dateString: string, isEnd = false): string => {
  if (!dateString) return '';

  const parts = dateString.split(/[-/]/);
  if (parts.length !== 3) return '';

  const day = parts[0].padStart(2, '0');
  const month = parts[1].padStart(2, '0');
  const year = parts[2];

  const time = isEnd ? '23:59:59' : '00:00:00';
  return `${year}-${month}-${day}T${time}Z`;
};

export const formatIsoToDDMMYYYY = (date: string | undefined) => {
  if (!date) return;
  const date1 = new Date(invertDateFormat(date, '/', '-'));
  const day = String(date1.getDate()).padStart(2, '0');
  const month = String(date1.getMonth() + 1).padStart(2, '0');
  const year = date1.getFullYear();

  return `${day}-${month}-${year}`;
};

export const addMonthsToDate = (date: Date, months: number): Date => {
  const currentDay = date.getDate();
  const newDate = new Date(date);
  newDate.setDate(1);
  newDate.setMonth(newDate.getMonth() + months);
  const daysInMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
  newDate.setDate(Math.min(currentDay, daysInMonth));
  return newDate;
};

export const isCurrentDayFromDayOfMonth = (dayOfMonth: number, date?: Date): boolean => {
  const dateToCheck = date || new Date();
  const currentDay = dateToCheck.getDate();

  return currentDay >= dayOfMonth;
};

export function formatAsDDMMYYYYInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

export function parseDDMMYYYY(value: string): Date | null {
  const match = DATE_DDMMYYYY_REGEX.exec(value);
  if (!match) return null;

  const dd = Number(match[1]);
  const mm = Number(match[2]);
  const yyyy = Number(match[3]);

  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;

  d.setHours(0, 0, 0, 0);
  return d;
}

export function validateOptionalDDMMYYYY(value: string | null): Date | null {
  if (!value) return null;
  if (!DATE_DDMMYYYY_REGEX.test(value)) return null;
  return parseDDMMYYYY(value);
}

export function isAfter(a: Date | null, b: Date | null): boolean {
  return !!(a && b && a.getTime() > b.getTime());
}

export function isBefore(a: Date | null, b: Date | null): boolean {
  return !!(a && b && a.getTime() < b.getTime());
}

export const getEndOfCurrentYearDDMMYYYY = (): string => {
  const now = new Date();
  const year = now.getFullYear();

  const endOfYear = new Date(year, 11, 31);
  const day = String(endOfYear.getDate()).padStart(2, '0');
  const month = String(endOfYear.getMonth() + 1).padStart(2, '0');

  return `${day}-${month}-${year}`;
};

export function validateDDMMYYYY(value: string): DDMMYYYYValidation {
  if (!DATE_DDMMYYYY_REGEX.test(value)) {
    return { ok: false, reason: 'FORMAT' };
  }

  const date = parseDDMMYYYY(value);
  if (!date) {
    return { ok: false, reason: 'INVALID_DATE' };
  }

  return { ok: true, date };
}

export const parseFlexibleDate = (dateString: string): Date | null => {
  if (!dateString) return null;

  if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
    if (!isoMatch) return null;

    const year = Number.parseInt(isoMatch[1], 10);
    const month = Number.parseInt(isoMatch[2], 10);
    const day = Number.parseInt(isoMatch[3], 10);

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    return date;
  }

  if (dateString.includes('/')) {
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;

    const [day, month, year] = parts.map((p) => Number.parseInt(p, 10));
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    return date;
  }

  if (dateString.includes('-') && /^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const parts = dateString.split('-');
    const [day, month, year] = parts.map((p) => Number.parseInt(p, 10));
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null;

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    return date;
  }

  return null;
};

export const formatDateByCountry = (date: Date, isUSA: boolean, outputSeparator: '/' | '-' = '/'): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  if (isUSA) {
    return `${month}${outputSeparator}${day}${outputSeparator}${year}`;
  }
  return `${day}${outputSeparator}${month}${outputSeparator}${year}`;
};

export const formatDateForDisplay = (dateString: string, isUSA = false, outputSeparator: '/' | '-' = '/'): string => {
  if (!dateString) return '';

  const date = parseFlexibleDate(dateString);
  if (!date) return '';

  return formatDateByCountry(date, isUSA, outputSeparator);
};

export const getMonthDateRange = (cutoffDay = 20): { startDate: string; endDate: string } => {
  const now = new Date();
  const currentDay = now.getUTCDate();

  const monthOffset = currentDay <= cutoffDay ? 1 : 2;

  const targetMonth = now.getUTCMonth() + monthOffset;
  const targetYear = now.getUTCFullYear();

  const startDate = new Date(Date.UTC(targetYear, targetMonth, 1, 0, 0, 0, 0));

  const endDate = new Date(Date.UTC(targetYear, targetMonth + 1, 0, 23, 59, 59, 999));

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  };
};
